import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Modal from "@/components/Modal/index";
import { useAppTheme } from "@/utils/theme";
import Box from "@mui/material/Box";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import inviteValidationSchema, { InviteFormData } from "./validation.schema";
import { useTranslation } from "react-i18next";
import Button from "@/components/Button";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import { ErrorResponse } from "@/domain/error.interface";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";
import { useUserStore } from "@/hooks/useUserStore";
import { useCreateAuditlog } from "@/hooks/useAuditLogs";
import {
  useColoringRequiredSetting,
  useColorModuleSetting,
  useCountrySetting,
  useDateOfBirthSetting,
  useParticipantReleaseDate,
  usePhoneNumberSetting,
  useStateIdSetting,
} from "@/hooks/useSetting";
import { useGetUserPrograms } from "@/hooks/usePrograms";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import { Typography } from "@mui/material";
import Datepicker from "@/components/Datepicker";
import dayjs, { Dayjs } from "dayjs";
import Select from "@/components/Select";
import TreatmentServiceAssignment from "./TreatmentServiceAssignment";
import AllocationEcho from "./AllocationEcho";
import SectionPanel from "./SectionPanel";
import ValidationBanner from "./ValidationBanner";
import Timepicker from "@/components/Timepicker";
import {
  readAppointmentValidationError,
  readFirstAppointmentConflict,
  readWallTime,
} from "@/utils/appointmentValidationError";
import {
  BLOCKED_FOOTER_REASON,
  buildConflictDetail,
  buildCorrectionHelper,
  CONFLICT_TITLE,
  isBlockedByConflict,
  isCorrectionAfterRelease,
  isCorrectionComplete,
  ReleaseDateConflict,
} from "./releaseDateConflictModel";
import { useOrganizationUnitsByIds } from "@/hooks/useOrganizations";
import { useUpdateClient } from "@/hooks/useClients";
import { ParticipantResponse } from "@/domain/participant.interface";
import { useCategoryByType } from "@/hooks/useCategories";
import { CategoryElement } from "@/domain/category.interface";

type ClientEditModalProps = {
  open: boolean;
  onClose: () => void;
  participant: ParticipantResponse;
  refetchParticipant: () => void;
};

const ClientEditModal: FC<ClientEditModalProps> = ({
  open,
  onClose,
  participant,
  refetchParticipant,
}) => {
  const { t } = useTranslation("common");
  const { userInfo, hasSystemAdminRole } = useUserStore();
  const { showSnackBar } = useFeedBack();
  const theme = useAppTheme();

  const createAuditLog = useCreateAuditlog();
  const updateClient = useUpdateClient();
  const isPending = updateClient.isPending;

  const { category, isLoadingCategory } = useCategoryByType({
    type: "COLORS",
    status: "ACTIVE",
    action: "edit",
  });

  const colors = useMemo(() => {
    if (!category) return [];

    return category?.categories.map((categoryElement: CategoryElement) => {
      const name = categoryElement.languages.find(
        (language) => language.language?.toLowerCase() === "en-us"
      )?.name as string;

      return {
        key: categoryElement._id,
        value: name,
        hexaColor: categoryElement.hexaColor,
        extra: (
          <div
            className="rounded-full w-6 h-6"
            style={{
              backgroundColor: categoryElement.hexaColor,
            }}
          ></div>
        ),
      };
    });
  }, [category]);

  const { programs, isLoadingPrograms } = useGetUserPrograms({
    userId: userInfo?._id as string,
    hasSystemAdminRole,
  });

  const programsOptions = useMemo(() => {
    if (!programs) return [];

    return programs.map((program) => ({
      key: program._id,
      value: program.name,
    }));
  }, [programs]);

  const [selectedOrgUnitIds, setSelectedOrgUnitIds] = useState<string[]>([]);

  /** Names for the 8b read-only echo, reported up by TreatmentServiceAssignment. */
  const [allocationNames, setAllocationNames] = useState<{
    agencyName?: string;
    locationName?: string;
  }>({});

  /**
   * PRET-1917 / design 8b — the release-date conflict, held as one piece of state because it drives
   * five things at once: the field's error outline, the banner beneath it, the correction section,
   * the footer's blocking reason, and SAVE.
   *
   * `requiresCorrection` is what distinguishes AC1 from an ordinary appointment rejection. It stays
   * true across a second refusal (a correction date that is itself invalid) so the section the
   * coordinator is typing into does not vanish underneath them.
   */
  const [conflict, setConflict] = useState<ReleaseDateConflict | null>(null);

  const {
    organizationUnits,
    isLoadingOrganizationUnits,
    refetchOrganizationUnits,
  } = useOrganizationUnitsByIds({
    ids: selectedOrgUnitIds,
    status: "ACTIVE",
  });

  const orgUnitsOptions = useMemo(() => {
    if (!organizationUnits) return [];

    if (hasSystemAdminRole) {
      return organizationUnits.map((orgUnit) => ({
        key: orgUnit._id,
        value: orgUnit.name,
      }));
    }

    if (!userInfo?.programs) return [];

    const userAccessibleOrgUnitIds = userInfo.programs.flatMap(
      (program) => program.orgUnitIds
    );

    const filteredOrgUnits = organizationUnits.filter((orgUnit) =>
      userAccessibleOrgUnitIds.includes(orgUnit._id)
    );

    return filteredOrgUnits.map((orgUnit) => ({
      key: orgUnit._id,
      value: orgUnit.name,
    }));
  }, [organizationUnits, hasSystemAdminRole, userInfo?.programs]);

  const countrySetting = useCountrySetting();
  const dateOfBirthSetting = useDateOfBirthSetting();
  const colorModuleSetting = useColorModuleSetting();
  const colorRequired = useColoringRequiredSetting();
  const stateIdSetting = useStateIdSetting();
  const phoneNumberSetting = usePhoneNumberSetting();

  const country = useMemo(() => countrySetting, [countrySetting]);
  const dateOfBirth = useMemo(() => dateOfBirthSetting, [dateOfBirthSetting]);
  const stateIdRequired = useMemo(() => stateIdSetting, [stateIdSetting]);
  const phoneNumberRequired = useMemo(
    () => phoneNumberSetting,
    [phoneNumberSetting]
  );
  const participantReleaseDateSetting = useParticipantReleaseDate();
  const coloringModule = useMemo(
    () => colorModuleSetting,
    [colorModuleSetting]
  );

  const coloringRequired = useMemo(() => colorRequired, [colorRequired]);
  const isParticipantReleaseDateMandatory = useMemo(
    () => participantReleaseDateSetting === "mandatory",
    [participantReleaseDateSetting]
  );
  const defaultColorIdValue = useCallback(() => {
    if (participant.lastEnrollment.colorId) {
      const existColor = colors.some(
        (color) => color.key === participant.lastEnrollment.colorId
      );

      if (existColor) {
        return participant.lastEnrollment.colorId;
      } else {
        return "random";
      }
    }

    if (coloringModule && coloringRequired) {
      return "random";
    }

    return null;
  }, [colors, participant, coloringModule, coloringRequired]);

  const {
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm<InviteFormData>({
    resolver: yupResolver(
      inviteValidationSchema(
        t,
        country,
        dateOfBirth,
        stateIdRequired,
        phoneNumberRequired,
        isParticipantReleaseDateMandatory,
        coloringModule,
        coloringRequired,
        // isEditMode. Omitting this defaulted it to false, so the schema still demanded the four
        // firstAppointment* fields -- which this modal does not render. Validation failed on
        // fields that are not on screen, so handleSubmit never called onSubmit: SAVE stayed
        // enabled, no error appeared, no request was sent, and the modal could not be saved at
        // all. That broke PRET-1837 AC1/AC2 (assigning the allocation on update) in the UI.
        true
      )
    ),
    mode: "all",
    defaultValues: {
      firstName: participant?.participant.firstName || "",
      lastName: participant?.participant.lastName || "",
      preferredName: participant?.participant.preferredName || "",
      email: participant?.participant.email || "",
      phoneNumber: participant?.participant.phoneNumber || "",
      dateOfBirth: participant?.participant.dateOfBirth || undefined,
      programId: participant?.lastEnrollment.programId || "",
      orgUnitIds: participant?.lastEnrollment.orgUnitId || "",
      caseNumbers: participant?.lastEnrollment.caseNumbers || "",
      externalId: participant?.lastEnrollment.externalId || "",
      status: participant?.participant.status || "",
      colorId: defaultColorIdValue() || participant?.lastEnrollment.colorId,
      releaseDate: participant?.lastEnrollment?.releaseDate || undefined,
      treatmentAgencyId: participant?.lastEnrollment?.treatmentAgencyId || "",
      locationId: participant?.lastEnrollment?.locationId || "",
    },
  });

  const programId = watch("programId") as string;
  const treatmentAgencyId = watch("treatmentAgencyId") as string;
  const locationId = watch("locationId") as string;
  const releaseDate = watch("releaseDate") as Date | string | undefined;
  const correctionDate = watch("firstAppointmentDate") as Dayjs | undefined;
  const correctionStart = watch("firstAppointmentStart") as Dayjs | undefined;
  const correctionEnd = watch("firstAppointmentEnd") as Dayjs | undefined;

  const correction = useMemo(
    () => ({
      date: correctionDate,
      start: correctionStart,
      end: correctionEnd,
    }),
    [correctionDate, correctionStart, correctionEnd]
  );

  const correctionAfterRelease = isCorrectionAfterRelease(
    correctionDate,
    releaseDate
  );
  const correctionComplete = isCorrectionComplete(correction, releaseDate);
  const blockedByConflict = isBlockedByConflict(
    conflict,
    correction,
    releaseDate
  );

  const getRandomColorId = useCallback(() => {
    if (!colors) return "";

    const randomIndex = Math.floor(Math.random() * colors.length);

    return colors[randomIndex].key;
  }, [colors]);

  const formatDateToISO = (date: string | number | Date) => {
    const d = new Date(date);
    return (
      d.getFullYear() +
      "-" +
      String(d.getMonth() + 1).padStart(2, "0") +
      "-" +
      String(d.getDate()).padStart(2, "0")
    );
  };

  /**
   * Routes an expected refusal to the field instead of the snackbar, and returns true when it
   * handled it.
   *
   * Called from BOTH the mutation's onError and the enclosing catch: `mutateAsync` rejects even
   * after onError has run, so handling it in one place only still let the toast through — the same
   * trap the invite modal documents.
   *
   * The bottom toast was the whole defect. It appears away from the field, says nothing about which
   * value is now wrong, and offers no way to fix it; 8b anchors the message to the release date and
   * opens the correction in place.
   */
  const showConflictInline = useCallback(
    (error: unknown): boolean => {
      // AC1 -- the release date invalidated the existing appointment.
      const cause = readFirstAppointmentConflict(error);
      if (cause) {
        setConflict({
          title: CONFLICT_TITLE,
          detail: buildConflictDetail(
            cause.firstAppointmentStart,
            cause.firstAppointmentEnd,
            allocationNames.agencyName
          ),
          requiresCorrection: true,
        });

        // 8b element e4 -- only the DATE is left blank. Times carry over from the appointment
        // being replaced, so the coordinator retypes the minimum. Wall time: the stored value
        // carries a Z it does not mean.
        const start = readWallTime(cause.firstAppointmentStart);
        const end = readWallTime(cause.firstAppointmentEnd);
        if (start) setValue("firstAppointmentStart", start);
        if (end) setValue("firstAppointmentEnd", end);

        return true;
      }

      // A correction date that is itself invalid, or a slot already taken. The section STAYS open —
      // the coordinator is mid-correction and needs the fields they are typing into.
      const message = readAppointmentValidationError(
        error,
        "The new first appointment date is not valid for this participant."
      );
      if (message !== null) {
        setConflict((previous) => ({
          title: message,
          requiresCorrection: previous?.requiresCorrection ?? false,
        }));

        return true;
      }

      return false;
    },
    [allocationNames.agencyName, setValue]
  );

  const onSubmit = async (data: InviteFormData) => {
    createAuditLog.mutate({
      appType: "WEB_BACK_OFFICE",
      module: MODULE_NAME.PARTICIPANTS_LIST,
      option: OPTIONS.PARTICIPANT_EDIT,
      actionCode: ACTION_CODE.WEB_PART_EDIT,
      action: "When saving a user (editing)",
      detail: `Edited the client ${data.firstName} ${data.lastName}`,
      transactionDate: new Date(),
      accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
      createdBy: userInfo?._id as string,
      targetId: participant.participant._id,
      programId: data.programId as string,
      orgUnitId: data.orgUnitIds as string,
    });

    const colorId =
      data.colorId === "random" ? getRandomColorId() : data.colorId;

    const payload = {
      firstName: data.firstName as string,
      lastName: data.lastName as string,
      preferredName: data.preferredName as string,
      email: data.email as string,
      countryPhoneCode: country === "US" ? "1" : "44",
      phoneNumber: data.phoneNumber as string,
      externalId: data.externalId as string,
      updatedBy: userInfo?._id as string,
      dateOfBirth: data.dateOfBirth
        ? formatDateToISO(data.dateOfBirth as string | number | Date)
        : undefined,
      programId: data.programId as string,
      orgUnitId: data.orgUnitIds as string,
      caseNumbers: data.caseNumbers as string,
      // Off-screen since 8b, so this is the persisted value travelling back unchanged. The
      // fallback matters: the field is no longer rendered, so nothing can repopulate it, and
      // writing an empty status would blank a real one on the record.
      status: (data.status as string) || participant.participant.status,
      releaseDate: data.releaseDate
        ? formatDateToISO(data.releaseDate as string | number | Date)
        : undefined,
      // PRET-1837 AC2 -- reassigning replaces the allocation in place.
      treatmentAgencyId: data.treatmentAgencyId as string,
      locationId: data.locationId as string,
      // PRET-1917 AC2 -- the moved release date and the replacement appointment go in ONE
      // request, so the backend applies both or neither. Sent only while the correction is
      // actually being asked for; an ordinary edit must not re-send the appointment it never
      // showed. The pickers hand back Dayjs; the API wants a calendar date and wall-clock times.
      ...(conflict?.requiresCorrection && correctionComplete
        ? {
            firstAppointmentDate: dayjs(
              data.firstAppointmentDate as Dayjs
            ).format("YYYY-MM-DD"),
            firstAppointmentStart: dayjs(
              data.firstAppointmentStart as Dayjs
            ).format("HH:mm:ss"),
            firstAppointmentEnd: dayjs(
              data.firstAppointmentEnd as Dayjs
            ).format("HH:mm:ss"),
          }
        : {}),
      ...(coloringModule && colorId ? { colorId: colorId } : {}),
    };

    try {
      await updateClient.mutateAsync(
        {
          participantId: participant.participant._id,
          enrollmentId: participant.lastEnrollment._id,
          editClient: payload,
        },
        {
          onSuccess: () => {
            onClose();
            refetchParticipant();
            showSnackBar("The client has been saved.", "success");
          },
          onError: (error: ErrorResponse) => {
            if (showConflictInline(error)) return;
            showSnackBar(
              (Array.isArray(error.message)
                ? error.message[0]
                : error.message) ||
                error.error ||
                "An error occurred"
            );
          },
        }
      );
    } catch (error: unknown) {
      if (showConflictInline(error)) return;
      const err = error as ErrorResponse;
      showSnackBar(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
          err.error ||
          "An error occurred"
      );
    }
  };

  useEffect(() => {
    if (selectedOrgUnitIds.length > 0) {
      refetchOrganizationUnits();
    }
  }, [selectedOrgUnitIds]);

  useEffect(() => {
    if (programs && programs.length > 0) {
      const selectedProgram = programs.find(
        (program) => program._id === programId
      );

      if (selectedProgram) {
        let orgUnitIds =
          selectedProgram.orgUnitIds || selectedProgram.orgUnitId || [];

        if (!hasSystemAdminRole && userInfo?.programs) {
          const userProgramAccess = userInfo.programs.find(
            (userProgram) => userProgram.programId === programId
          );

          if (userProgramAccess) {
            orgUnitIds = orgUnitIds.filter((orgUnitId) =>
              userProgramAccess.orgUnitIds.includes(orgUnitId)
            );
          } else {
            orgUnitIds = [];
          }
        }

        setSelectedOrgUnitIds(orgUnitIds);
      } else {
        setSelectedOrgUnitIds([]);
      }
    }
  }, [programs, programId, hasSystemAdminRole, userInfo?.programs]);

  useEffect(() => {
    if (colors.length > 0) {
      setValue("colorId", defaultColorIdValue());
    }
  }, [colors, participant, defaultColorIdValue]);

  /**
   * Reverting the release date withdraws the whole conflict — banner, correction section and all.
   *
   * The Epic asks for this explicitly; PRET-1917 has no AC covering it and no frame draws it, so it
   * is recorded as an interpretation in the design map's `unverified` list. Keyed on the release
   * date ALONE: typing in the correction fields must not dismiss the refusal that asked for them.
   */
  const lastSeenReleaseDate = useRef(releaseDate);
  useEffect(() => {
    // Only on an actual CHANGE. Firing on mount would clear a field nothing has set yet and, more
    // to the point, would write to the form during the first commit.
    if (lastSeenReleaseDate.current === releaseDate) return;
    lastSeenReleaseDate.current = releaseDate;

    setConflict(null);
    setValue("firstAppointmentDate", undefined);
  }, [releaseDate, setValue]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      isPending={false}
      title="Edit client"
      theme={theme}
      size={{
        width: "100%",
        minHeight: 440,
        maxHeight: 780,
        maxWidth: 860,
      }}
      content={
        <Box sx={{ width: "100%", minHeight: 380, padding: "20px 0" }}>
          <Grid container spacing={"20px"}>
            {/*
              The personal-information block is NOT rendered here.
              ============================================================
              Frame 8b draws the edit modal as PRET information -> the release-date conflict ->
              the correction -> the allocation echo. There is no name / email / date-of-birth /
              phone block on it at all, and the developer confirmed against the frame that it is
              not wanted: those details are captured at invitation and changed through their own
              flows, so carrying them here made the coordinator scroll past six fields they had
              not come to change before reaching the one they had.

              The VALUES still travel. They are seeded into the form from `participant` via
              `defaultValues` and go out in the payload unchanged, so hiding the inputs is not the
              same as clearing the fields — an update still sends the participant's real name and
              email.

              Which is exactly why `validation.schema.ts` relaxes `dateOfBirth` and `phoneNumber`
              in edit mode. Both are conditionally REQUIRED by account settings, and a participant
              can legitimately have neither on record. Left required, the resolver would reject on
              a field that is no longer on screen, `handleSubmit` would never reach `onSubmit`, and
              SAVE would sit there enabled doing nothing with no message — the same silent failure
              the `isEditMode` comment above already documents for the appointment fields.
            */}
            <Grid item xs={12} sm={12}>
              <Typography
                variant="titleSmall"
                fontWeight={700}
                gutterBottom
                color={theme.palette.menu.dark}
              >
                PRET information
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="programId"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <Select
                    handleChange={(value) => {
                      field.onChange(value);
                      // Clear org unit selection when program changes
                      setValue("orgUnitIds", "");
                    }}
                    data={programsOptions || []}
                    value={field.value as string}
                    label="Program"
                    required
                    id="client-program"
                    isLoading={isLoadingPrograms}
                    fullWidth
                    error={!!errors.programId}
                    errorMessage={errors.programId?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="orgUnitIds"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <Select
                    handleChange={(value) => {
                      field.onChange(value);
                    }}
                    data={orgUnitsOptions || []}
                    value={field.value as string}
                    label="Org unit"
                    required
                    id="client-orgUnitIds"
                    isLoading={!programId || isLoadingOrganizationUnits}
                    fullWidth
                    error={!!errors.orgUnitIds}
                    errorMessage={errors.orgUnitIds?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="caseNumbers"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    id="caseNumbers"
                    label="State ID"
                    variant="outlined"
                    className="flex flex-1"
                    value={field.value}
                    required={stateIdRequired === "mandatory"}
                    onChange={(value) => field.onChange(value)}
                    error={!!errors.caseNumbers}
                    helperText={
                      errors.caseNumbers ? errors.caseNumbers.message : null
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="externalId"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    id="externalId"
                    label="External ID"
                    variant="outlined"
                    className="flex flex-1"
                    value={field.value}
                    onChange={(value) => field.onChange(value)}
                    error={!!errors.externalId}
                    helperText={
                      errors.externalId ? errors.externalId.message : null
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="releaseDate"
                control={control}
                render={({ field }) => (
                  <div>
                    <Datepicker
                      {...field}
                      label="Release date"
                      onChange={(value) => {
                        field.onChange(
                          value !== undefined ? value?.toDate() : undefined
                        );
                      }}
                      value={
                        field.value
                          ? dayjs(field.value as string | number | Date | Dayjs)
                          : null
                      }
                      // 8b element e1 -- the error is anchored to the field that CAUSED it. A
                      // rejected release date leaves the field looking accepted otherwise, and
                      // the coordinator has to guess which of a dozen inputs to change.
                      error={!!errors.releaseDate || Boolean(conflict)}
                      helperText={errors.releaseDate?.message || ""}
                      required={isParticipantReleaseDateMandatory}
                      disableFuture={false}
                      // A release date is a FUTURE event — the date the participant is due out.
                      // The picker was offering days already past (on the 9th it accepted the
                      // 1st), which is not a date anyone can be released on. Greys them out
                      // rather than accepting one and failing later, the same way the first
                      // appointment's Date greys out everything up to the release date.
                      minDate={dayjs().startOf("day")}
                    />
                  </div>
                )}
              />
            </Grid>

            {/*
              8b element e2 -- the conflict named and the existing appointment restated, directly
              beneath the field that caused it. Full width because it carries two sentences; a
              half-width banner in the field's own column wraps to four lines.
            */}
            {conflict ? (
              <Grid item xs={12}>
                <ValidationBanner
                  severity="error"
                  title={conflict.title}
                  detail={conflict.detail}
                />
              </Grid>
            ) : null}

            {/*
              8b element e3 -- the correction, in place. Rendered ONLY while blocked: its absence is
              the design's statement for AC3 (a release-date move that leaves the appointment valid
              saves with no further input), per the PO ruling of 2026-09-08.
            */}
            {conflict?.requiresCorrection ? (
              <SectionPanel
                title="New first appointment"
                required
                subtitle={buildCorrectionHelper(releaseDate)}
              >
                <Grid item xs={12} sm={4}>
                  <Controller
                    name="firstAppointmentDate"
                    control={control}
                    render={({ field }) => (
                      <Datepicker
                        {...field}
                        label="Date"
                        value={field.value ? dayjs(field.value as Dayjs) : null}
                        onChange={(value) => field.onChange(value)}
                        // The schema leaves this optional in edit mode, so the day-after-release
                        // rule is enforced here instead of by yup. minDate greys out the
                        // impossible days; the message covers a typed-in date, which the picker
                        // still accepts.
                        error={
                          Boolean(correctionDate) && !correctionAfterRelease
                        }
                        helperText={
                          correctionDate && !correctionAfterRelease
                            ? "Must be after the release date."
                            : ""
                        }
                        required
                        disableFuture={false}
                        minDate={
                          releaseDate
                            ? dayjs(releaseDate).add(1, "day")
                            : undefined
                        }
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Controller
                    name="firstAppointmentStart"
                    control={control}
                    render={({ field }) => (
                      <Timepicker
                        {...field}
                        label="Start"
                        value={field.value ? dayjs(field.value as Dayjs) : null}
                        onChange={(value) => field.onChange(value)}
                        required
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Controller
                    name="firstAppointmentEnd"
                    control={control}
                    render={({ field }) => (
                      <Timepicker
                        {...field}
                        label="End"
                        value={field.value ? dayjs(field.value as Dayjs) : null}
                        onChange={(value) => field.onChange(value)}
                        required
                      />
                    )}
                  />
                </Grid>

                {/*
                  Element e5, INSIDE this panel rather than loose below it.
                  ==========================================================
                  On the frame the tinted card with its rounded corners encloses the heading, the
                  helper, the three fields AND the agency/location echo — one section, because the
                  echo is telling the coordinator where THIS replacement appointment is committed.
                  Rendered outside the panel it read as an unrelated fourth block sharing an edge
                  with it.

                  Same placement the invite modal already uses: `FirstAppointment.tsx` puts the
                  echo inside its own SectionPanel for 8a, for the same reason.

                  It is no longer rendered in the unblocked state, and nothing is lost by that —
                  the Treatment service assignment section below shows the same agency and location
                  in its selects whenever there is no correction to commit.
                */}
                <Grid item xs={12}>
                  <AllocationEcho
                    agency={allocationNames.agencyName}
                    location={allocationNames.locationName}
                  />
                </Grid>
              </SectionPanel>
            ) : null}

            {/*
              No Status select. It is not on frame 8b, and it was misleading where it stood: the
              dropdown offers only Active and Paused, while a participant can be in any of ten
              states (INVITED, PENDING_VERIFICATION, COMPLETED, DISENROLLED, ...). So for most
              participants it rendered BLANK — the persisted value simply is not one of its two
              options — which reads as an unset required field rather than as a control that does
              not apply. That is what prompted "no sé a qué pertenece".

              The participant's real status still travels: it is seeded into the form from the
              record and sent back unchanged (see the payload's `status`, which falls back to the
              persisted value). Pausing or reactivating a participant is a separate action, not a
              side effect of editing their details.
            */}

            <TreatmentServiceAssignment
              control={control}
              errors={errors}
              setValue={setValue}
              accountId={userInfo?.accountId}
              userId={userInfo?._id}
              open={open}
              selectedAgencyId={treatmentAgencyId}
              selectedLocationId={locationId}
              onNamesResolved={setAllocationNames}
            />

            {coloringModule ? (
              <Grid item xs={12} sm={6}>
                <Controller
                  name="colorId"
                  control={control}
                  defaultValue={defaultColorIdValue}
                  render={({ field }) => (
                    <Select
                      handleChange={(value) => field.onChange(value)}
                      data={[{ key: "random", value: "(Random)" }, ...colors]}
                      value={field.value as string}
                      label="Screening Color"
                      id="participant-screening-color"
                      isLoading={isLoadingCategory}
                      required={coloringRequired}
                      showClearOption={!coloringRequired}
                      renderValue={(selected) => {
                        if (selected === "random") return "(Random)";

                        return (
                          <div className="flex gap-2.5">
                            <div
                              className="rounded-full w-6 h-6"
                              style={{
                                backgroundColor: colors.find(
                                  (color) => color.key === selected
                                )?.hexaColor,
                              }}
                            ></div>
                            {
                              colors.find((color) => color.key === selected)
                                ?.value
                            }
                          </div>
                        );
                      }}
                      error={!!errors.colorId}
                      errorMessage={errors.colorId?.message}
                    />
                  )}
                />
              </Grid>
            ) : null}
          </Grid>
        </Box>
      }
      actions={
        <Box
          sx={{
            display: "flex",
            gap: "10px",
            alignItems: "center",
            width: "100%",
          }}
        >
          {/*
            8b element e6 -- the reason SAVE is unavailable is written out on the left, not hidden
            on hover. A disabled button with no stated reason is the failure mode the frame is
            correcting.
          */}
          {blockedByConflict ? (
            <Typography
              variant="bodySmall"
              sx={{ color: "error.dark", mr: "auto" }}
            >
              {BLOCKED_FOOTER_REASON}
            </Typography>
          ) : null}
          <Box
            sx={{
              display: "flex",
              gap: "10px",
              alignItems: "center",
              ml: "auto",
            }}
          >
            <Button
              variantType="cancel"
              onClick={onClose}
              disabled={isPending}
              label="CANCEL"
            />
            <Button
              variantType="save"
              onClick={handleSubmit(onSubmit)}
              disabled={isPending || blockedByConflict}
              isLoading={isPending}
              label="SAVE"
            />
          </Box>
        </Box>
      }
    />
  );
};

export default ClientEditModal;
